import * as React from 'react';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import FormControl from '@mui/material/FormControl';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { TextField } from '@mui/material';
import { createTheme, ThemeProvider, useTheme } from '@mui/material/styles';
import { outlinedInputClasses } from '@mui/material/OutlinedInput';

interface FloatingFieldProps {
    label: string;
    value: string;
    error?: string | undefined;
    isPassword?: boolean;
    fieldDescription?: string;
    onChange: (...event: any[]) => void;
    disabled?: boolean;
    readOnly?: boolean;
}

const customTheme = (outerTheme: any) =>
    createTheme({
        palette: {
            mode: outerTheme.palette.mode,
        },
        components: {
            MuiTextField: {
                styleOverrides: {
                    root: {
                        '--TextField-brandBorderColor': '#D1D5DB',
                        '--TextField-brandBorderHoverColor': '#B2BAC2',
                        '--TextField-brandBorderFocusedColor': '#142456',
                        '& label.Mui-focused': {
                            color: 'var(--TextField-brandBorderFocusedColor)',
                        },
                    },
                },
            },
            MuiOutlinedInput: {
                styleOverrides: {
                    notchedOutline: {
                        borderColor: 'var(--TextField-brandBorderColor)',
                    },
                    root: {
                        [`&:hover .${outlinedInputClasses.notchedOutline}`]: {
                            borderColor: 'var(--TextField-brandBorderHoverColor)',
                        },
                        [`&.Mui-focused .${outlinedInputClasses.notchedOutline}`]: {
                            borderColor: 'var(--TextField-brandBorderFocusedColor)',
                        },
                    },
                },
            },
        },
    });

export const FloatingField = ({
    label,
    value,
    error = undefined,
    isPassword = false,
    onChange,
    fieldDescription,
    disabled = false,
    readOnly = false,
}: FloatingFieldProps) => {
    const [showPassword, setShowPassword] = React.useState(false);
    const outerTheme = useTheme();

    const handleClickShowPassword = () => setShowPassword((show) => !show);

    return (
        <ThemeProvider theme={customTheme(outerTheme)}>
            <FormControl fullWidth sx={{ my: 2 }} variant="outlined">
                <TextField
                    error={error ? true : false}
                    id="outlined-error-helper-text"
                    label={label}
                    {...(disabled || readOnly ? { value } : { defaultValue: value })}
                    type={!showPassword && isPassword ? 'password' : 'text'}
                    helperText={error || fieldDescription}
                    disabled={disabled}
                    InputProps={{
                        readOnly,
                        endAdornment: isPassword && (
                            <InputAdornment position="end">
                                <IconButton aria-label="toggle password visibility" onClick={handleClickShowPassword} edge="end">
                                    {!showPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                    onChange={onChange}
                />
            </FormControl>
        </ThemeProvider>
    );
};
